import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  IconButton,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Cancel,
  RateReview,
  Schedule,
} from '@mui/icons-material';
import LoadingButton from '../../../components/LoadingButton';
import axios from 'axios';
import { hostname, parseError } from '../../../utils';

interface ContentReviewDialogProps {
  open: boolean;
  onClose: () => void;
  content: any;
  companyId: string;
  getToken: () => Promise<string>;
  onSuccess?: () => void;
  eventListener: (event: any) => void;
}

const ContentReviewDialog: React.FC<ContentReviewDialogProps> = ({
  open,
  onClose,
  content,
  companyId,
  getToken,
  onSuccess,
  eventListener,
}) => {
  const [status, setStatus] = useState(content?.assignedTo?.status || 'pending');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleStatusChange = (event: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    if (newStatus !== null) {
      setStatus(newStatus);
    }
  };

  const handleSubmitReview = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/content/${content.id}/assignment-status`,
        {
          companyId,
          status,
          notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      eventListener({
        type: 'SET_SUCCESS',
        message: 'Review submitted successfully',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      eventListener({
        type: 'SET_ERROR',
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'in_review':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle />;
      case 'rejected':
        return <Cancel />;
      case 'in_review':
        return <RateReview />;
      default:
        return <Schedule />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Review Content</Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {content && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                <strong>Content:</strong> {content.title}
              </Typography>
              <Typography variant="caption">
                Type: {content.type} | Current Status: {content.status}
              </Typography>
            </Alert>
          )}

          {content?.assignedTo && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Assignment Details:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2">
                  <strong>Assigned by:</strong> {content.uid}
                </Typography>
                <Typography variant="body2">
                  <strong>Assigned on:</strong>{' '}
                  {content.assignedTo.assignedDate
                    ? new Date(content.assignedTo.assignedDate).toLocaleDateString()
                    : 'N/A'}
                </Typography>
                {content.assignedTo.notes && (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Instructions:</strong> {content.assignedTo.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              Review Status:
            </Typography>
            <ToggleButtonGroup
              value={status}
              exclusive
              onChange={handleStatusChange}
              fullWidth
              sx={{ mt: 1 }}
            >
              <ToggleButton value="pending" disabled>
                <Schedule sx={{ mr: 1 }} />
                Pending
              </ToggleButton>
              <ToggleButton value="in_review">
                <RateReview sx={{ mr: 1 }} />
                In Review
              </ToggleButton>
              <ToggleButton value="approved" color="success">
                <CheckCircle sx={{ mr: 1 }} />
                Approved
              </ToggleButton>
              <ToggleButton value="rejected" color="error">
                <Cancel sx={{ mr: 1 }} />
                Rejected
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes"
            placeholder={
              status === 'rejected'
                ? 'Please provide reasons for rejection and suggestions for improvement...'
                : 'Add your review comments here...'
            }
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            required={status === 'rejected'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          onClick={handleSubmitReview}
          variant="contained"
          color={getStatusColor(status)}
          disabled={status === 'rejected' && !notes}
        >
          Submit Review
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ContentReviewDialog;
